import XcPanelHeader from "@electricMotorShop/Widgets/Basic/XcPanelHeader";
import XcPanelRow, { XcPanelRowPresentationProps } from "@electricMotorShop/Widgets/Basic/XcPanelRow";
import XcPanelShopAll from "@electricMotorShop/Widgets/Basic/XcPanelShopAll";
import XcPanelStandard from "@electricMotorShop/Widgets/Basic/XcPanelStandard";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface XcPanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<XcPanelMenuPresentationProps>;
        children?: StyledProp<XcPanelMenuPresentationProps>;
        menu?: StyledProp<XcPanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: XcPanelRowPresentationProps;
}

export type XcPanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        panelTitle?: string;
        panelUrl?: string;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    > button {
        width: 100%;
    }
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 126px);
    overflow-y: auto;
    background: ${({ color, theme }) => resolveColor(color, theme)};
    flex: 1;
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    display: flex;
    flex-direction: column;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

export interface XcPanelMenuStyles {
    panelTitleWrapper?: InjectableCss;
    titleButtonWrapper?: InjectableCss;
    panelTitle?: TypographyPresentationProps;
    panelBackClickable?: ClickablePresentationProps;
    panelBackIcon?: IconPresentationProps;
    panelCloseClickable?: ClickablePresentationProps;
    panelCloseIcon?: IconPresentationProps;
    xcPanelRow?: InjectableCss;
}
const panelMenuStyles: XcPanelMenuStyles = {
    panelTitleWrapper: {
        css: css`        
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 5px 0;
            background: ${getColor("primary.main")};    
            color: ${getColor("primary.contrast")};
        `,
    },
    titleButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
        `,
    },
    panelTitle: {
        css: css`
            margin-left: 4px;
            font-size: 18px;
            font-weight: 600;
            display: inline-block;   
            color: ${getColor("primary.contrast")};
        `,
    },
    panelBackClickable: {
        css: css`
            width: 80%;
            background: ${getColor("primary.main")}; 
        `,
    },
    panelBackIcon: {
        color: "white",
        css: css`
        `,
    },
    panelCloseClickable: {
        css: css`
            width: 20%;
        `,
    },
    panelCloseIcon: {
        color: "white",
        css: css`
        `,
    },
    xcPanelRow: {
        css: css`
            background: ${getColor("primary.contrast")};
            color: ${getColor("text.main")};
            padding: 0;
        `,
    },
}

const styles = panelMenuStyles;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type XcPanelMenuProps = XcPanelMenuComponentProps & XcPanelMenuPresentationProps;

type XcPanelMenuPropsCompleted = XcPanelMenuPresentationProps &
    Omit<XcPanelMenuComponentProps, "maxDepth"> &
    Required<Pick<XcPanelMenuComponentProps, "maxDepth">>;

class XcPanelMenu extends React.Component<XcPanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
    };

    UNSAFE_componentWillReceiveProps(nextProps: XcPanelMenuPropsCompleted) {
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any) {
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const panelRowProps = applyProp("panelRowProps");

        const currentUrlRegex = new RegExp(currentUrl!, "gi");
        return (
            <>
                {childMenuItems
                    .filter(item => !item.excludeFromNavigation)
                    .map(item => {
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth;
                        const clickableProps: { href?: string } = { ...item.clickableProps };
                        if (!renderChildren && item.url) {
                            clickableProps.href = item.url;
                        }
                        const menuItem = (
                            <XcPanelRow
                                key={item.title}
                                isCurrent={item.url?.match(currentUrlRegex) || item.title?.match(currentUrlRegex)}
                                onClick={this.props.closeOverlay}
                                color={applyProp("bodyColor")}
                                hasChildren={renderChildren || false}
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                                css={css`
                                    width: 100%;
                                `}
                                {...clickableProps}
                                {...panelRowProps}
                            >
                                <XcPanelStandard
                                    panelTitle={item.title}
                                    hasChildren={renderChildren || false}
                                    data-test-selector="XcPanelStandard"
                                />
                            </XcPanelRow>
                        );
                        if (renderChildren) {
                            return (
                                <XcPanelMenu
                                    {...this.props}
                                    as="div"
                                    key={item.title}
                                    panelTrigger={menuItem}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                    panelTitle={item.title}
                                    panelUrl={item.url}
                                />
                            );
                        }
                        return menuItem;
                    })}
            </>
        );
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                            >                                
                                {/* This is the top row with the back arrow and close button */}
                                <XcPanelHeader 
                                    panelTitle={this.props.panelTitle || ""} 
                                    layer={layer}
                                    closeOverlay={this.props.closeOverlay}
                                    moveNextRight={this.moveNextRight}
                                />
                                {/* This is the 'Shop All' row with a button to the category items */}
                                {
                                    this.props.panelTitle !== "Menu" && 
                                    this.props.panelTitle !== "Categories" && 
                                    this.props.panelTitle !== "All Categories" && 
                                    this.props.panelTitle !== undefined && (
                                        <XcPanelRow 
                                            {...styles.xcPanelRow}
                                            hasChildren={false}
                                        >
                                            <XcPanelShopAll 
                                                panelTitle={this.props.panelTitle}
                                                panelUrl={this.props.panelUrl}
                                                closeOverlay={this.props.closeOverlay}
                                            />
                                        </XcPanelRow>
                                    )
                                }
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={applyProp("bodyColor", "common.accent")}
                                    data-test-selector="XcPanelMenu_panelMenuStyle"
                                >
                                    {menuItems ? (
                                        this.renderMenuItems(menuItems, 1, propBuilder)
                                    ) : (
                                        <>{this.props.children}</>
                                    )}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

XcPanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: XcPanelMenuProps) => <XcPanelMenu {...(props as XcPanelMenuPropsCompleted)} />);
